import { useCallback, useEffect, useRef, useState } from 'react';
import useClassroomStore from '../../stores/classroomStore';
import { getYouTubeEmbedUrl } from '../presentation-editor/youtubeUtils';

// Sync a YouTube iframe using YouTube's built-in postMessage protocol
// (no SDK script load required — same wire format the SDK uses under the hood).
// Reuses the existing `sendVideoState` / `syncedVideoStates` machinery so
// backend and polling stay the same as for regular <video> elements.

const DRIFT_THRESHOLD = 0.8; // seconds — YouTube seek granularity is coarser than <video>
const HEARTBEAT_MS = 2000;

// YouTube player states (from IFrame API docs)
const YT_STATE_ENDED = 0;
const YT_STATE_PLAYING = 1;
const YT_STATE_PAUSED = 2;

const YT_ORIGINS = new Set([
  'https://www.youtube.com',
  'https://www.youtube-nocookie.com',
]);

const SyncedYouTubeVideo = ({
  elementId,
  videoId,
  className,
  isTeacher,
  lessonSessionId,
  autoplay,
  mute,
  loop,
}) => {
  const iframeRef = useRef(null);
  const currentTimeRef = useRef(0);
  const playerStateRef = useRef(-1);
  const lastLocalPushRef = useRef(0);
  const [isReady, setIsReady] = useState(false);

  const sendVideoState = useClassroomStore((s) => s.sendVideoState);
  const remoteState = useClassroomStore((s) => s.syncedVideoStates?.[elementId]);

  const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
  // Students must start muted so YouTube's cross-origin autoplay policy lets
  // `playVideo` postMessage commands succeed without a per-iframe user gesture.
  // A document-level gesture listener below sends `unMute` on the first click.
  const embedUrl = getYouTubeEmbedUrl(videoId, {
    autoplay: isTeacher && autoplay,
    mute: isTeacher ? mute : true,
    loop,
    enablejsapi: true,
    controls: isTeacher,
    disablekb: !isTeacher,
    origin,
  });

  const sendCommand = useCallback((func, args = []) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func, args }),
        '*'
      );
    } catch (_) {}
  }, []);

  const pushTeacherState = useCallback((overrides = {}) => {
    if (!isTeacher || !lessonSessionId) return;
    sendVideoState(lessonSessionId, elementId, {
      isPlaying: playerStateRef.current === YT_STATE_PLAYING,
      currentTime: currentTimeRef.current,
      ...overrides,
    });
    lastLocalPushRef.current = Date.now();
  }, [isTeacher, lessonSessionId, elementId, sendVideoState]);

  // Attach one window-level message listener; filter by iframe source
  useEffect(() => {
    const handleMessage = (event) => {
      if (!YT_ORIGINS.has(event.origin)) return;
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) return;

      let data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (_) {
        return;
      }
      if (!data || typeof data !== 'object') return;

      if (data.event === 'onReady' || data.event === 'initialDelivery') {
        setIsReady(true);
      }

      // YouTube pushes state changes and current time via infoDelivery
      if (data.event === 'infoDelivery' && data.info) {
        if (typeof data.info.currentTime === 'number') {
          const prev = currentTimeRef.current;
          const next = data.info.currentTime;
          const wasPlaying = playerStateRef.current === YT_STATE_PLAYING;
          const now = Date.now();
          const timeSincePush = lastLocalPushRef.current
            ? (now - lastLocalPushRef.current) / 1000
            : 999;
          currentTimeRef.current = next;
          // Teacher-detected seek: currentTime jumped further than plausible drift
          if (isTeacher && wasPlaying && Math.abs(next - prev) > Math.max(1, timeSincePush * 2)) {
            pushTeacherState();
          }
        }
        if (typeof data.info.playerState === 'number') {
          const prev = playerStateRef.current;
          const next = data.info.playerState;
          playerStateRef.current = next;
          if (
            isTeacher &&
            prev !== next &&
            (next === YT_STATE_PLAYING || next === YT_STATE_PAUSED || next === YT_STATE_ENDED)
          ) {
            pushTeacherState({ isPlaying: next === YT_STATE_PLAYING });
          }
        }
      }

      // onStateChange sometimes arrives standalone (e.g. buffering transitions)
      if (data.event === 'onStateChange' && typeof data.info === 'number') {
        const prev = playerStateRef.current;
        const next = data.info;
        playerStateRef.current = next;
        if (
          isTeacher &&
          prev !== next &&
          (next === YT_STATE_PLAYING || next === YT_STATE_PAUSED || next === YT_STATE_ENDED)
        ) {
          pushTeacherState({ isPlaying: next === YT_STATE_PLAYING });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isTeacher, pushTeacherState]);

  // Kick off the "listening" handshake once the iframe loads so YouTube
  // starts pushing infoDelivery events (currentTime + playerState).
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'listening', id: elementId, channel: 'widget' }),
        '*'
      );
    } catch (_) {}
  }, [elementId]);

  // Teacher heartbeat — refresh (currentTime, anchor) while playing so students
  // don't extrapolate from a stale anchor over long plays.
  useEffect(() => {
    if (!isTeacher || !lessonSessionId) return;
    const interval = setInterval(() => {
      if (playerStateRef.current !== YT_STATE_PLAYING) return;
      pushTeacherState();
    }, HEARTBEAT_MS);
    return () => clearInterval(interval);
  }, [isTeacher, lessonSessionId, pushTeacherState]);

  // Student iframe embeds with mute=1 so autoplay works. Unmute on the first
  // user gesture anywhere in the lesson so the student hears the audio the
  // moment they interact with the page.
  useEffect(() => {
    if (isTeacher) return;
    let unmuted = false;
    const unmuteOnGesture = () => {
      if (unmuted) return;
      unmuted = true;
      sendCommand('unMute');
      document.removeEventListener('pointerdown', unmuteOnGesture, true);
      document.removeEventListener('touchstart', unmuteOnGesture, true);
      document.removeEventListener('keydown', unmuteOnGesture, true);
    };
    document.addEventListener('pointerdown', unmuteOnGesture, true);
    document.addEventListener('touchstart', unmuteOnGesture, true);
    document.addEventListener('keydown', unmuteOnGesture, true);
    return () => {
      document.removeEventListener('pointerdown', unmuteOnGesture, true);
      document.removeEventListener('touchstart', unmuteOnGesture, true);
      document.removeEventListener('keydown', unmuteOnGesture, true);
    };
  }, [isTeacher, sendCommand]);

  // Student: mirror remote state — seek if drifted, play/pause to match
  useEffect(() => {
    if (isTeacher || !remoteState || !isReady) return;

    const anchor = remoteState.anchorAt || 0;
    const elapsed = remoteState.isPlaying && anchor
      ? (Date.now() - anchor) / 1000
      : 0;
    const expected = (remoteState.currentTime || 0) + elapsed;

    if (Math.abs(currentTimeRef.current - expected) > DRIFT_THRESHOLD) {
      sendCommand('seekTo', [expected, true]);
    }

    if (remoteState.isPlaying && playerStateRef.current !== YT_STATE_PLAYING) {
      sendCommand('playVideo');
    } else if (!remoteState.isPlaying && playerStateRef.current === YT_STATE_PLAYING) {
      sendCommand('pauseVideo');
    }
  }, [remoteState, isTeacher, isReady, sendCommand]);

  if (!videoId) return null;

  return (
    <div className={`relative ${className || ''}`}>
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title="YouTube video"
        className="w-full h-full"
        frameBorder="0"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        onLoad={handleIframeLoad}
      />
      {!isTeacher && (
        // Block student clicks so they can't pause/scrub the local iframe
        <div className="absolute inset-0 cursor-not-allowed" aria-hidden="true" />
      )}
    </div>
  );
};

export default SyncedYouTubeVideo;
